import {
  Card,
  Typography,
  Stack,
  IconButton,
  Avatar,
  Divider,
  Box,
  Tooltip,
  Skeleton,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScaleIcon from '@mui/icons-material/Scale';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

import { CompobotType } from 'types/compobots.type';
import React, { useState } from 'react';
import { theme as themeStatic } from '../../theme';
import { useTheme } from '@mui/material/styles';
import CompobotCardSkeleton from './CompobotCardSkeleton';

interface Props {
  compobot?: CompobotType;
  onOpenQr: () => void;
  goToGrafana: (deviceId?: string, page?: string) => void;
  isLoading?: boolean;
  onViewLogs?: () => void;
}

export default function CompobotCard({ compobot, onOpenQr, goToGrafana, isLoading, onViewLogs }: Props) {
  const theme = useTheme();

  if (isLoading) return <CompobotCardSkeleton />;
  if (!compobot) return null;

  const {
    deviceId,
    organisation,
    currentWeight,
    temperature,
    humidity,
    lastInsertTime,
    lastQrScanTime,
  } = compobot;

  const orgName = organisation?.companyName ?? 'Ismeretlen cég';
  const score = organisation?.score ?? 0;
  const [logoError, setLogoError] = useState(false);


  const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/?$/, '') ?? '';
  const logoSrc = organisation?.logoPath && !logoError ? `${apiBase}/public/${organisation.logoPath}` : undefined;
  console.log('organisation.logoPath:', organisation?.logoPath);

  function formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const parts = new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(date);

    const getPart = (type: string) => parts.find(p => p.type === type)?.value ?? '';
    return `${getPart('year')}.${getPart('month')}.${getPart('day')} ${getPart('hour')}:${getPart('minute')}`;
  }

  function formatWeight(value?: number): string {
    if (value === undefined || value === null) return '-';

    if (Math.abs(value) >= 1000) {
      const kg = value / 1000;
      const [intPart, decimalPart] = kg.toFixed(1).split('.');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${grouped}.${decimalPart} kg`;
    } else {
      const [intPart, decimalPart] = value.toFixed(1).split('.');
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${grouped}.${decimalPart} g`;
    }
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: 3,
        boxShadow: 1,
        transition: '0.2s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        paddingBottom: 0,
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            alt={orgName}
            src={logoSrc}
            sx={{
              width: 48,
              height: 48,
              bgcolor: !logoSrc ? themeStatic.palette.primary.main : 'transparent',
            }}
            onError={() => {
              setLogoError(true);
            }}
          >
            {(!logoSrc || logoError) && orgName[0]}
          </Avatar>
          <div>
            <Typography variant="subtitle2" fontWeight={600}>
              {deviceId}{' '}
              {compobot.isOnline !== undefined && (
                <Typography
                  component="span"
                  variant="subtitle2"
                  color={compobot.isOnline ? 'success.main' : 'error.main'}
                  fontWeight={600}
                  sx={{ ml: 1 }}
                >
                  ({compobot.isOnline ? 'Online' : 'Offline'})
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {orgName}
            </Typography>
          </div>
        </Stack>

        <Divider />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            rowGap: 1,
            columnGap: 2,
          }}
        >
          <Typography variant="body2">
            <Tooltip title="Pontszám">
              <EmojiEventsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{score}</strong>
          </Typography>
          <Typography variant="body2">
            <Tooltip title="Súly">
              <ScaleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{formatWeight(currentWeight)}</strong>
          </Typography>
          <Typography variant="body2">
            <Tooltip title="Hőmérséklet">
              <ThermostatIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{temperature !== undefined && temperature !== null ? temperature.toFixed(1) : '-'} °C</strong>
          </Typography>
          <Typography variant="body2">
            <Tooltip title="Páratartalom">
              <OpacityIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{humidity !== undefined && humidity !== null ? humidity.toFixed(1) : '-'} %</strong>
          </Typography>
          <Typography variant="body2" >
            <Tooltip title="Utolsó bedobás">
              <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{formatDate(lastInsertTime) ?? '-'}</strong>
          </Typography>
          <Typography variant="body2" >
            <Tooltip title="Utolsó QR">
              <QrCodeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: theme.palette.primary.main }} />
            </Tooltip>{' '}
            <strong>{formatDate(lastQrScanTime) ?? '-'}</strong>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} mt={2} justifyContent="center">
          <Tooltip title="Logs">
            <IconButton
              aria-label="Logs"
              onClick={() => goToGrafana(deviceId, 'logs')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                padding: '6px 12px',
                minWidth: 36,
                minHeight: 36,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <ListAltIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Telemetry">
            <IconButton
              aria-label="Telemetry"
              onClick={() => goToGrafana(deviceId, 'telemetry')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                padding: '6px 12px',
                minWidth: 36,
                minHeight: 36,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <ShowChartIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Scans">
            <IconButton
              aria-label="Scans"
              onClick={() => onViewLogs?.()}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                padding: '6px 12px',
                minWidth: 36,
                minHeight: 36,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <SmartphoneIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="QR">
            <IconButton
              aria-label="QR"
              onClick={onOpenQr}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                padding: '6px 12px',
                minWidth: 36,
                minHeight: 36,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff',
                },
              }}
            >
              <QrCodeIcon />
            </IconButton>
          </Tooltip>

        </Stack>
      </Stack>
    </Card>
  );
}
